<template>
  <div class="hud-container">
    <div class="hud-top">
      <div v-if="gameMode === 'FreeForAll'" class="game-mode">
        <span class="mode-label">Free-For-All</span>
      </div>
      <div v-else class="team-scores">
        <div class="team red">
          <span class="team-name">Red Team</span>
          <span class="team-score">{{ redTeamScore }}</span>
        </div>
        <div class="team blue">
          <span class="team-name">Blue Team</span>
          <span class="team-score">{{ blueTeamScore }}</span>
        </div>
      </div>
      <div :class="['match-time', {'time-warning': matchTimeSeconds <= 60}]">{{ formattedTime }}</div>
    </div>
    
    <!-- Match countdown overlay -->
    <div v-if="showCountdown" class="countdown-overlay">
      <div class="countdown-container">
        <div class="countdown-label">Match starts in</div>
        <div class="countdown-number">{{ countdownSeconds }}</div>
      </div>
    </div>
    
    <div class="hud-bottom">
      <HealthBar :current-health="playerHealth" :max-health="maxHealth" />
      <WeaponSelector :current-weapon="currentWeapon" :available-weapons="availableWeapons" />
      <div class="ammo-display">
        <span class="current-ammo">{{ currentAmmo }}</span>
        <span class="ammo-separator">/</span>
        <span class="max-ammo">{{ maxAmmo }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import HealthBar from './HealthBar.vue';
import WeaponSelector from './WeaponSelector.vue';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'GameHUD',
  components: {
    HealthBar,
    WeaponSelector
  },
  props: {
    redTeamScore: {
      type: Number,
      default: 0
    },
    blueTeamScore: {
      type: Number,
      default: 0
    },
    matchTimeSeconds: {
      type: Number,
      default: 300 // 5 minutes by default (300 seconds)
    },
    showCountdown: {
      type: Boolean,
      default: false
    },
    countdownSeconds: {
      type: Number,
      default: 0
    },
    playerHealth: {
      type: Number,
      default: 100
    },
    maxHealth: {
      type: Number,
      default: 100
    },
    currentWeapon: {
      type: String,
      default: 'Rifle'
    },
    availableWeapons: {
      type: Array,
      default: () => ['Rifle', 'Pistol', 'Knife']
    },
    currentAmmo: {
      type: Number,
      default: 30
    },
    maxAmmo: {
      type: Number,
      default: 90
    },
    gameMode: {
      type: String,
      default: ''
    }
  },
  computed: {
    formattedTime() {
      const minutes = Math.floor(this.matchTimeSeconds / 60);
      const seconds = this.matchTimeSeconds % 60;
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }
  }
});
</script>

<style scoped>
.hud-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Arial', sans-serif;
  color: white;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
}

.hud-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.team-scores {
  display: flex;
  gap: 20px;
}

.team {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 15px;
  border-radius: 5px;
  background-color: rgba(0, 0, 0, 0.5);
}

.team.red {
  border-left: 4px solid #f44336;
}

.team.blue {
  border-left: 4px solid #2196f3;
}

.team-score {
  font-size: 1.5em;
  font-weight: bold;
}

.game-mode {
  padding: 5px 15px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  border-left: 4px solid #ff9800;
}

.mode-label {
  font-size: 1.2em;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.match-time {
  font-size: 1.5em;
  font-weight: bold;
  padding: 5px 15px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  min-width: 70px;
  text-align: center;
  transition: all 0.3s ease;
}

.match-time.time-warning {
  background-color: rgba(244, 67, 54, 0.7);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.countdown-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 100;
}

.countdown-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 30px;
  border-radius: 15px;
  border: 2px solid #ffffff;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
}

.countdown-label {
  font-size: 1.5em;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.countdown-number {
  font-size: 5em;
  font-weight: bold;
  color: #ff9800;
  text-shadow: 0 0 10px rgba(255, 152, 0, 0.7);
  animation: countdown-pulse 1s infinite;
}

@keyframes countdown-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.hud-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ammo-display {
  font-size: 1.5em;
  padding: 5px 15px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  display: flex;
  gap: 5px;
}

.current-ammo {
  font-weight: bold;
}

.ammo-separator {
  opacity: 0.7;
}
</style> 